import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Link, Breadcrumbs as MuiBreadcrumbs, Typography } from "@mui/material";
import React from "react";
import { Link as RouterLink, useMatches } from "react-router-dom";
import { useTranslation } from "react-i18next";

export interface RouteMatch {
  id: string;
  pathname: string;
  data: any;
  params: Record<string, string>;
  handle?: {
    crumb: (match: RouteMatch) => CrumbData;
    parents?: readonly { readonly label: string; readonly pathname: string }[];
    phoneBack?: boolean;
  };
}

export interface CrumbData extends RouteMatch {
  label: string;
  labelKey?: string;
  labelFallback?: string;
}

const PLAIN_SX = { pb: 1 } as const;
const DESKTOP_ONLY_SX = { pb: 1, display: { xs: 'none', md: 'block' } } as const;

const Breadcrumbs: React.FC = () => {
  const { t } = useTranslation();
  const matches = useMatches() as RouteMatch[];

  const crumbs = matches
    .filter((match) => Boolean(match.handle?.crumb))
    .flatMap((match) => [
      ...(match.handle!.parents ?? []).map(
        (parent): CrumbData => ({
          id: `${match.id}:${parent.pathname}`,
          pathname: parent.pathname,
          data: undefined,
          params: {},
          label: parent.label,
          labelKey: `breadcrumbs.${parent.label}`,
          labelFallback: parent.label,
        }),
      ),
      match.handle!.crumb(match),
    ]);

  const displayLabel = (crumb: CrumbData) =>
    crumb.labelKey ? t(crumb.labelKey, crumb.labelFallback ?? crumb.label) : crumb.label;

  const crumbsLength = crumbs?.length;
  if (crumbsLength < 2) {
    return null;
  }
  const desktopOnly = matches.some((match) => match.handle?.phoneBack === true);

  return (
    <>
      <MuiBreadcrumbs sx={desktopOnly ? DESKTOP_ONLY_SX : PLAIN_SX} separator={<NavigateNextIcon fontSize="small" />} aria-label={t('globalComponents.breadcrumb.ariaLabel', 'breadcrumb')}>
        {crumbs.map((crumb, index) => {
          const isLast = index === crumbs.length - 1;
          return isLast ? (
            <Typography
              key={crumb.pathname}
              component="span"
              aria-current="page"
              color="text.primary"
              variant={crumbsLength === 1 ? 'title' : 'h4'}
              sx={{ display: "flex", alignItems: "center" }}
            >
              {displayLabel(crumb)}
            </Typography>
          ) : (
            <Link
              component={RouterLink}
              to={crumb.pathname}
              key={crumb.pathname}
              color="inherit"
              sx={{ display: "flex", alignItems: "center" }}
            >
              <Typography component="span" variant={crumbsLength === 1 ? "title" : "h4"}>{displayLabel(crumb)}</Typography>
            </Link>
          );
        })}
      </MuiBreadcrumbs>
    </>
  );
};

export default Breadcrumbs;